import React, { useCallback } from 'react';

import { Session } from '@yandex-video-platform/goloom-sdk';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { batchActions } from 'redux-batched-actions';

import { RootState } from '../../../../../../init-store';
import { getIsOneOnOneEnabled } from '../../../../../shared/features/config/selectors';
import { selectIsRoomAgentShellEnabled } from '../../../../../shared/features/flags/selectors';
import {
    getIsVideoShouldBeMirrored,
    getShowMyVideo,
    selectHideParticipantsVideo,
} from '../../../../../shared/features/settings';
import { isMobileUa } from '../../../../../shared/features/uatraits';
import { useActions } from '../../../../../shared/hooks';
import { GRID_VIEW } from '../../../constants';
import { selectIsFullscreen } from '../../../features/fullscreen';
import {
    nextGridPageAction,
    prevGridPageAction,
    setCurrentGridPageAction,
    setMaxDisplayParticipants,
    setVideoContainerSize,
} from '../../../features/goloom-video-grid';
import {
    getActiveGridView,
    getCallee,
    getCallStatus,
    getCurrentPage,
    getDesktopSharingStatus,
    getGridItemRatio,
    getIsWaitingForCallee,
    getMaxDisplayParticipants,
    getParticipantsCount,
    selectEffectiveSlotsGridView,
    selectIsNewGrid,
    selectMaxSlotsCount,
    selectShouldRenderInviteBlock,
    selectShowNextSlotsPageButton,
    selectShowPrevSlotsPageButton,
} from '../../../selectors';
import { calcMaxDisplayParticipants } from '../helpers/calc-max-display-participants';
import { SlotsRenderer } from './slots-renderer';

interface Props {
    session: Session;
}

const mapStateToProps = (state: RootState) => {
    const desktopSharingStatus = getDesktopSharingStatus(state);
    const isFullscreen = selectIsFullscreen(state);
    const props = {
        maxDisplayParticipants: getMaxDisplayParticipants(state),
        currentPage: getCurrentPage(state),
        activeGridView: getActiveGridView(state),
        itemRatio: getGridItemRatio(state),
        showMyVideo: getShowMyVideo(state),
        participantCount: getParticipantsCount(state),
        isMeScreenSharing: desktopSharingStatus === 'local',
        isFullscreen: isFullscreen,
        isMirrorVideo: getIsVideoShouldBeMirrored(state),
        isScreenSharingActive: desktopSharingStatus !== 'none',
    };
    if (isFullscreen) {
        props.maxDisplayParticipants = 0;
        props.showMyVideo = false;
    }
    return props;
};

export const GoloomVideoGrid: React.FC<Props> = ({ session }) => {
    const {
        maxDisplayParticipants,
        currentPage,
        showMyVideo,
        activeGridView,
        itemRatio,
        participantCount,
        isMeScreenSharing,
        isMirrorVideo,
        isScreenSharingActive,
        isFullscreen,
    } = useSelector(mapStateToProps, shallowEqual);
    const dispatch = useDispatch();

    const {
        nextGridPageAction: handleNextPage,
        prevGridPageAction: handlePrevPage,
        setContainerSize,
    } = useActions({
        nextGridPageAction,
        prevGridPageAction,
        setContainerSize: setVideoContainerSize,
    });

    const needShowMyVideo = showMyVideo || participantCount <= 1;
    const needShowSelfScreenSharing = isMeScreenSharing && activeGridView === GRID_VIEW.PRESENTER;
    const shouldRenderInviteBlock = useSelector(selectShouldRenderInviteBlock);

    const oneOnOne = useSelector(getIsOneOnOneEnabled);
    const callee = useSelector(getCallee);
    const isWaitingForCallee = useSelector(getIsWaitingForCallee);
    const callStatus = useSelector(getCallStatus);
    const isNewGrid = useSelector(selectIsNewGrid);
    const isMobile = useSelector(isMobileUa);
    const effectiveGridView = useSelector(selectEffectiveSlotsGridView);
    const isRoomAgentShellEnabled = useSelector(selectIsRoomAgentShellEnabled);
    const showPrevPageButton = useSelector(selectShowPrevSlotsPageButton);
    const showNextPageButton = useSelector(selectShowNextSlotsPageButton);
    const maxSlotsCount = useSelector(selectMaxSlotsCount);

    const changeMaxDisplayParticipants = useCallback(
        (elem?: Element) => {
            if (activeGridView === GRID_VIEW.PRESENTER && !isFullscreen) {
                const count = calcMaxDisplayParticipants({
                    containerWidth: elem?.clientWidth,
                    containerHeight: elem?.clientHeight,
                    isNewGrid,
                    isMobile,
                });

                if (count !== maxDisplayParticipants) {
                    dispatch(batchActions([setMaxDisplayParticipants(count), setCurrentGridPageAction(0)]));
                }
            }
        },
        [dispatch, activeGridView, maxDisplayParticipants, isFullscreen, isNewGrid, isMobile]
    );

    const onResize = useCallback(
        (elem?: Element) => {
            if (elem) {
                setContainerSize({ width: elem.clientWidth, height: elem.clientHeight });
            }
            changeMaxDisplayParticipants(elem);
        },
        [changeMaxDisplayParticipants, setContainerSize]
    );

    const hideParticipantsVideo = useSelector(selectHideParticipantsVideo);

    return (
        <SlotsRenderer
            session={session}
            gridView={effectiveGridView}
            itemRatio={itemRatio}
            shouldRenderInviteBlock={shouldRenderInviteBlock}
            maxDisplayParticipants={maxDisplayParticipants}
            currentPage={currentPage}
            needShowMyVideo={needShowMyVideo}
            needShowSelfScreenSharing={needShowSelfScreenSharing}
            onNextPageClick={handleNextPage}
            onPrevPageClick={handlePrevPage}
            isMirrorVideo={isMirrorVideo}
            isScreenSharingActive={isScreenSharingActive}
            onResize={onResize}
            isOneOnOne={oneOnOne}
            callee={callee}
            isWaitingForCallee={isWaitingForCallee}
            callStatus={callStatus}
            hideParticipantsVideo={hideParticipantsVideo}
            isNewGrid={isNewGrid}
            showPrevPageButton={showPrevPageButton}
            showNextPageButton={showNextPageButton}
            maxSlotsCount={maxSlotsCount}
            isShellEnabled={isRoomAgentShellEnabled}
        />
    );
};
